import { createFocusTrap } from "../utils/focus-trap";
import { applyReducedMotionVideos, prefersReducedMotion } from "../utils/reduced-motion";

export default class ArticleModal {
  constructor() {
    this.modal = null;
    this.closeButton = null;
    this.isOpen = false;
    this.originalUrl = null;
    this.videoObserver = null;
    this.lastFocusedElement = null;
    this.focusTrap = null;
    this.init();
  }

  init() {
    this.createModal();
    this.attachEventListeners();
  }

  createModal() {
    const modalHTML = `
      <div class="c-article-modal" id="article-modal" role="dialog" aria-modal="true" aria-label="Article" aria-hidden="true">
        <div class="c-article-modal__backdrop js-article-modal-backdrop"></div>
        <button type="button" class="c-article-modal__close js-article-modal-close" aria-label="Close article">CLOSE</button>
        <div class="c-article-modal__container">
          <div class="c-article-modal__content js-article-modal-content">
            <!-- Content will be loaded here -->
          </div>
        </div>
      </div>
    `;
    
    document.body.insertAdjacentHTML('beforeend', modalHTML);
    this.modal = document.getElementById('article-modal');
    this.closeButton = document.querySelector('.js-article-modal-close');
    this.backdrop = document.querySelector('.js-article-modal-backdrop');
    this.focusTrap = createFocusTrap(this.modal);
  }

  attachEventListeners() {
    // Close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    // Close on backdrop click
    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.close());
    }

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Handle browser back/forward buttons
    window.addEventListener('popstate', (e) => {
      if (this.isOpen) {
        // If modal is open and user navigates back, close modal
        this.modal.classList.remove('is-active');
        this.isOpen = false;
        document.body.classList.remove('no-scroll');
        this.originalUrl = null;
      }
    });

    // Prevent body scroll when modal is open
    if (this.modal) {
      this.modal.addEventListener('transitionend', () => {
        if (this.isOpen) {
          document.body.classList.add('no-scroll');
        } else {
          document.body.classList.remove('no-scroll');
        }
      });
    }
  }

  async loadArticle(url) {
    try {
      const response = await fetch(url);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Extract article content
      const articleContent = doc.querySelector('.c-article-content');
      if (articleContent) {
        return articleContent.innerHTML;
      }
      
      // Fallback: try to get main content
      const mainContent = doc.querySelector('main');
      if (mainContent) {
        return mainContent.innerHTML;
      }
      
      return '';
    } catch (error) {
      console.error('Error loading article:', error);
      return '<div class="c-article-modal__body"><p>Error loading article content.</p></div>';
    }
  }

  optimizeMedia(container) {
    if (!container) return;

    // Reset observer when modal content is replaced
    if (this.videoObserver) {
      this.videoObserver.disconnect();
      this.videoObserver = null;
    }

    const videos = container.querySelectorAll("video");
    if (videos.length === 0) return;

    this.videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          const shouldAutoplay =
            video.dataset.articleAutoplay === "true" && !prefersReducedMotion();

          if (entry.isIntersecting && entry.intersectionRatio > 0.35) {
            if (shouldAutoplay) {
              const playAttempt = video.play();
              if (playAttempt && typeof playAttempt.catch === "function") {
                playAttempt.catch(() => {});
              }
            }
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      {
        threshold: [0, 0.35, 0.75],
      }
    );

    videos.forEach((video, index) => {
      const hasAutoplay = video.hasAttribute("autoplay");
      video.dataset.articleAutoplay = hasAutoplay ? "true" : "false";

      // Avoid eager network cost for stacked videos in long articles
      if (!video.getAttribute("preload") || video.getAttribute("preload") === "auto") {
        video.setAttribute("preload", index === 0 ? "metadata" : "none");
      }

      // Prevent all videos from starting at once on load
      video.removeAttribute("autoplay");
      video.pause();

      this.videoObserver.observe(video);
    });
  }

  async open(url) {
    if (!this.modal) return;

    this.lastFocusedElement = document.activeElement;

    // Make modal visible first (but still transparent) so backdrop can render
    this.modal.style.visibility = 'visible';
    
    // Force a reflow to ensure initial state is rendered
    this.modal.offsetHeight;
    
    // Use requestAnimationFrame to ensure browser has painted initial state
    // This ensures smooth transition start without delay
    requestAnimationFrame(() => {
      this.modal.classList.add('is-active');
      this.modal.setAttribute('aria-hidden', 'false');
      this.isOpen = true;
      document.body.classList.add('no-scroll');
      this.focusTrap.activate();
      if (this.closeButton) {
        this.closeButton.focus();
      }
    });
    
    const contentContainer = this.modal.querySelector('.js-article-modal-content');
    if (contentContainer) {
      contentContainer.innerHTML = '<div style="text-align: center; padding: 2rem;">Loading...</div>';
      
      const articleHTML = await this.loadArticle(url);
      contentContainer.innerHTML = articleHTML;
      applyReducedMotionVideos(contentContainer);
      this.optimizeMedia(contentContainer);
    }

    // Update URL without reloading - store original URL for restoration
    if (history.pushState) {
      this.originalUrl = window.location.href;
      history.pushState({ modal: true, articleUrl: url }, '', url);
    }
  }

  close() {
    if (!this.modal) return;

    this.modal.classList.remove('is-active');
    this.modal.setAttribute('aria-hidden', 'true');
    this.isOpen = false;
    document.body.classList.remove('no-scroll');
    this.focusTrap.deactivate();

    if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
      this.lastFocusedElement.focus();
    }
    this.lastFocusedElement = null;

    if (this.videoObserver) {
      this.videoObserver.disconnect();
      this.videoObserver = null;
    }

    // Restore original URL without reloading
    if (history.pushState && this.originalUrl) {
      history.pushState({}, '', this.originalUrl);
      this.originalUrl = null;
    } else if (history.pushState) {
      // Fallback: go back to shift page
      const shiftUrl = window.location.origin + '/shift';
      history.pushState({}, '', shiftUrl);
    }
  }

  destroy() {
    if (this.videoObserver) {
      this.videoObserver.disconnect();
      this.videoObserver = null;
    }
    if (this.modal) {
      this.modal.remove();
    }
  }
}

